import React, { useState, useEffect } from 'react';
import { useAuth, leadApi, apiClient } from '@/lib/api';
import { toast } from 'sonner';
import { LastActivityBadge } from '@/components/LastActivityBadge';
import { LeadOriginStamp } from '@/components/LeadOriginStamp';
import { NumberConflictBadge } from '@/components/NumberConflictBadge';
import { apiErrorMessage } from '@/lib/apiError';
import { SlaCountdownChip } from '@/components/SlaCountdownChip';
import SlaWatchdogPanel from '@/components/SlaWatchdogPanel';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '@/components/ui/dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
    DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { ScrollArea } from '@/components/ui/scroll-area';
import ImportButton from '@/components/ImportButton';
import ReminderModal from '@/components/ReminderModal';
import { PeriodFilter } from '@/components/PeriodFilter';
import { Pagination } from '@/components/Pagination';
import EnrollmentPaymentModal from '@/components/EnrollmentPaymentModal';
import RejectionReasonModal from '@/components/RejectionReasonModal';
import ReactivateLeadModal from '@/components/ReactivateLeadModal';
import { ClickToCall, CallHistory } from '@/components/ClickToCall';
import InlineSecondaryPhoneEditor from '@/components/InlineSecondaryPhoneEditor';
import { SecondaryPhoneField } from '@/components/LeadDetailModal';
import AdditionalNumbersField from '@/components/AdditionalNumbersField';
import LastPickupBadge, { useLastPickup, pickupForPhone } from '@/components/LastPickupBadge';
import { COUNTRIES, LEAD_SOURCES, detectCountryFromPhone } from '@/lib/phoneCountry';
import {
    DndContext,
    DragOverlay,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
    useDroppable,
} from '@dnd-kit/core';
import {
    SortableContext,
    useSortable,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
    Plus,
    Search,
    Phone,
    Mail,
    MapPin,
    Calendar,
    MoreVertical,
    AlertTriangle,
    User,
    UserPlus,
    Clock,
    Bell,
    PhoneCall,
    GripVertical,
    Target,
    GitMerge,
    Package,
    ShoppingCart,
    CheckCircle,
    LayoutGrid,
    List,
    Send,
    MessageSquare,
    CalendarDays,
} from 'lucide-react';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import logger from '@/lib/logger';

const LEAD_STAGES = [
    { id: 'new_lead', label: 'New Lead', color: 'bg-blue-500' },
    { id: 'call_back', label: 'Call Back', color: 'bg-purple-500' },
    { id: 'warm_lead', label: 'Warm Lead', color: 'bg-yellow-500' },
    { id: 'hot_lead', label: 'Hot Lead', color: 'bg-orange-600' },
    { id: 'enrolled', label: 'Enrolled', color: 'bg-emerald-500' },
];

// Optional column rendered ONLY for super_admin (and admin) so they can
// audit rejected leads directly from the Kanban without leaving the
// pipeline view. Sales executives, team leaders, and sales managers
// never see it — rejected leads are otherwise reached via the lead-pool
// view, the rejection report, or the lead-detail dialog.
const REJECTED_STAGE = { id: 'rejected', label: 'Rejected', color: 'bg-rose-500' };

const ALL_LEAD_STAGES = [
    ...LEAD_STAGES,
    { id: 'not_interested', label: 'Not Interested', color: 'bg-gray-500' },
    { id: 'rejected', label: 'Rejected', color: 'bg-rose-500' },
];

const REJECTION_REASONS = [
    { id: 'price', label: 'Price' },
    { id: 'timing', label: 'Timing' },
    { id: 'no_money', label: 'No Money' },
    { id: 'wrong_number', label: 'Wrong Number' },
    { id: 'duplicate', label: 'Duplicate' },
    { id: 'not_interested', label: 'Not Interested' },
];

const LeadCallRecordings = ({ leadId }) => {
    const [calls, setCalls] = React.useState([]);
    const [loading, setLoading] = React.useState(true);
    const [playingId, setPlayingId] = React.useState(null);
    const [audioUrl, setAudioUrl] = React.useState(null);
    const API_URL = process.env.REACT_APP_BACKEND_URL;

    React.useEffect(() => {
        if (!leadId) return;
        setLoading(true);
        apiClient.get(`/leads/${leadId}/calls`).then(r => setCalls(r.data || [])).catch(() => {}).finally(() => setLoading(false));
    }, [leadId]);

    const handlePlay = (call) => {
        if (!call.recording_id) return;
        if (playingId === call.id) {
            setPlayingId(null);
            setAudioUrl(null);
            return;
        }
        const token = localStorage.getItem('access_token');
        setAudioUrl(`${API_URL}/api/calls/${call.recording_id}/audio?token=${token}`);
        setPlayingId(call.id);
    };

    if (loading) return <div className="mt-4 text-xs text-muted-foreground">Loading calls...</div>;

    return (
        <div className="mt-4" data-testid="lead-call-recordings">
            <div className="flex items-center gap-2 mb-2">
                <PhoneCall className="h-4 w-4 text-primary" />
                <Label className="text-sm font-medium">Call Recordings</Label>
                <Badge variant="secondary" className="text-[10px]">{calls.length}</Badge>
            </div>
            {/* Audio Player - shown when playing */}
            {audioUrl && (
                <div className="mb-2 p-2 rounded-lg bg-primary/5 border border-primary/20">
                    <audio
                        src={audioUrl}
                        controls
                        autoPlay
                        onEnded={() => { setPlayingId(null); setAudioUrl(null); }}
                        onError={() => { toast.error('Failed to play recording'); setPlayingId(null); setAudioUrl(null); }}
                        className="w-full h-8"
                        style={{ minHeight: '32px' }}
                        data-testid="audio-player"
                    />
                </div>
            )}
            <div className="space-y-1.5 max-h-[200px] overflow-y-auto">
                {calls.length === 0 ? (
                    <p className="text-xs text-muted-foreground py-3 text-center">No 3CX calls found for this lead</p>
                ) : calls.map(call => (
                    <div key={call.id} className="flex items-center gap-2 p-2 rounded-md border bg-card text-xs hover:bg-muted/50 transition-colors" data-testid={`call-entry-${call.id}`}>
                        <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${call.direction === 'Inbound' ? 'bg-blue-500/15 text-blue-500' : 'bg-emerald-500/15 text-emerald-500'}`}>
                            <PhoneCall className="h-3 w-3" />
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-1.5">
                                <span className="font-medium truncate">{call.agent_name || 'Unknown'}</span>
                                <span className="text-muted-foreground">{call.direction === 'Inbound' ? 'IN' : 'OUT'}</span>
                            </div>
                            <span className="text-muted-foreground">{call.call_date ? new Date(call.call_date).toLocaleDateString('en-AE', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : ''}</span>
                        </div>
                        <span className="text-muted-foreground flex-shrink-0">{call.duration_display}</span>
                        {call.has_recording ? (
                            <Button size="sm" variant={playingId === call.id ? 'default' : 'ghost'} className="h-7 w-7 p-0 flex-shrink-0" onClick={() => handlePlay(call)} data-testid={`play-btn-${call.id}`}>
                                {playingId === call.id ? (
                                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><rect x="6" y="4" width="4" height="16"/><rect x="14" y="4" width="4" height="16"/></svg>
                                ) : (
                                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z"/></svg>
                                )}
                            </Button>
                        ) : (
                            <span className="text-muted-foreground/50 flex-shrink-0 text-[10px]">No audio</span>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
};

// Compact reassign dropdown for the Sales Table view's Agent column.
// Renders the current agent (or a "+ Assign" button when unassigned) and
// pops a searchable agent list on click. Same call flow as the Kanban
// card — `onQuickReassign(lead, agent)` — so a single handler powers both.
const TableReassignCell = ({ lead, availableAgents, onQuickReassign, canReassign }) => {
    const [open, setOpen] = React.useState(false);
    const [query, setQuery] = React.useState('');
    const ref = React.useRef(null);

    React.useEffect(() => {
        if (!open) return;
        const onDocClick = (e) => {
            if (ref.current && !ref.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener('mousedown', onDocClick);
        return () => document.removeEventListener('mousedown', onDocClick);
    }, [open]);

    const filtered = React.useMemo(() => {
        const q = query.trim().toLowerCase();
        const list = availableAgents || [];
        if (!q) return list;
        return list.filter(a => (a.full_name || '').toLowerCase().includes(q));
    }, [availableAgents, query]);

    if (!canReassign) {
        return <span className="text-xs">{lead.assigned_to_name || '—'}</span>;
    }

    return (
        <div ref={ref} className="relative inline-block" onClick={(e) => e.stopPropagation()}>
            <button
                type="button"
                onClick={(e) => { e.stopPropagation(); setOpen(v => !v); setQuery(''); }}
                className={`text-xs inline-flex items-center gap-1 px-2 py-1 rounded-md border border-transparent hover:border-border hover:bg-accent transition-colors ${lead.assigned_to_name ? 'text-foreground' : 'text-blue-500'}`}
                data-testid={`table-reassign-btn-${lead.id}`}
            >
                {lead.assigned_to_name ? (
                    <>
                        <User className="h-3 w-3" />
                        <span>{lead.assigned_to_name}</span>
                    </>
                ) : (
                    <>
                        <UserPlus className="h-3 w-3" />
                        <span>Assign</span>
                    </>
                )}
            </button>
            {open && (
                <div
                    className="absolute z-50 top-8 left-0 bg-popover border rounded-md shadow-lg min-w-[220px] max-h-72 flex flex-col"
                    onClick={(e) => e.stopPropagation()}
                    data-testid={`table-reassign-menu-${lead.id}`}
                >
                    <div className="p-1.5 border-b">
                        <Input
                            autoFocus
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            placeholder="Search agent…"
                            className="h-7 text-xs"
                            data-testid={`table-reassign-search-${lead.id}`}
                        />
                    </div>
                    <div className="overflow-y-auto py-1">
                        {filtered.length === 0 && (
                            <div className="text-xs text-muted-foreground px-2 py-1.5">No agents found</div>
                        )}
                        {filtered.map(agent => (
                            <button
                                key={agent.id}
                                type="button"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onQuickReassign(lead, agent);
                                    setOpen(false);
                                }}
                                className={`w-full text-left text-xs px-2 py-1.5 hover:bg-accent ${agent.id === lead.assigned_to ? 'bg-accent font-medium' : ''}`}
                                data-testid={`table-reassign-agent-${lead.id}-${agent.id}`}
                            >
                                {agent.full_name}
                                {agent.team_name && (
                                    <span className="text-[10px] text-muted-foreground ml-1">· {agent.team_name}</span>
                                )}
                            </button>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

const LeadCard = ({ lead, onUpdate, onView, onSetReminder, isDragging, isSuperAdmin, availableAgents, onQuickReassign }) => {
    const [showReassign, setShowReassign] = React.useState(false);
    // Local optimistic override for inline secondary-phone edits so the card
    // reflects the new value immediately without waiting for a kanban refetch.
    const [secondaryOverride, setSecondaryOverride] = React.useState(null);
    React.useEffect(() => { setSecondaryOverride(null); }, [lead?.id, lead?.secondary_phone]);
    const effectiveSecondary = (secondaryOverride !== null ? secondaryOverride : lead.secondary_phone) || '';
    const formatDate = (dateStr) => {
        if (!dateStr) return 'N/A';
        return new Date(dateStr).toLocaleDateString('en-AE', {
            day: 'numeric',
            month: 'short',
            hour: '2-digit',
            minute: '2-digit',
        });
    };

    const formatCurrency = (amount) => {
        if (!amount) return null;
        return new Intl.NumberFormat('en-AE', {
            style: 'currency',
            currency: 'AED',
            minimumFractionDigits: 0,
        }).format(amount);
    };

    const hasReminder = lead.reminder_date && !lead.reminder_completed;
    const isPipelineStage = ['warm_lead', 'hot_lead'].includes(lead.stage);
    const isClosedLead = lead.stage === 'enrolled' || lead.stage === 'rejected';
    const closedCardStyle = lead.stage === 'enrolled'
        ? 'border-l-4 border-l-emerald-500 bg-emerald-500/5'
        : lead.stage === 'rejected'
        ? 'border-l-4 border-l-rose-500 bg-rose-500/5'
        : '';

    return (
        <div
            className={`kanban-card stage-${lead.stage} animate-fade-in ${isDragging ? 'opacity-50 shadow-lg ring-2 ring-primary' : ''} ${closedCardStyle}`}
            onClick={() => !isDragging && onView(lead)}
            data-testid={`lead-card-${lead.id}`}
        >
            <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-2">
                    <div className="cursor-grab active:cursor-grabbing text-muted-foreground hover:text-foreground">
                        <GripVertical className="h-4 w-4" />
                    </div>
                    <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center text-white text-sm font-medium">
                        {lead.full_name?.charAt(0) || '?'}
                    </div>
                    <div>
                        <p className="font-medium text-sm">{lead.full_name}</p>
                        {lead.assigned_to_name && (
                            <div className="relative">
                                {isSuperAdmin ? (
                                    <button
                                        className="text-xs text-blue-500 hover:text-blue-700 flex items-center gap-1 hover:underline"
                                        onClick={(e) => { e.stopPropagation(); setShowReassign(!showReassign); }}
                                        data-testid={`reassign-btn-${lead.id}`}
                                    >
                                        <User className="h-3 w-3" />
                                        {lead.assigned_to_name}
                                    </button>
                                ) : (
                                    <p className="text-xs text-muted-foreground flex items-center gap-1">
                                        <User className="h-3 w-3" />
                                        {lead.assigned_to_name}
                                    </p>
                                )}
                                {showReassign && isSuperAdmin && (
                                    <div className="absolute z-50 top-6 left-0 bg-popover border rounded-md shadow-lg p-1 min-w-[180px] max-h-48 overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                                        {(availableAgents || []).map(agent => (
                                            <button
                                                key={agent.id}
                                                className={`w-full text-left text-xs px-2 py-1.5 rounded hover:bg-accent ${agent.id === lead.assigned_to ? 'bg-accent font-medium' : ''}`}
                                                onClick={(e) => { e.stopPropagation(); onQuickReassign(lead, agent); setShowReassign(false); }}
                                                data-testid={`reassign-agent-${agent.id}`}
                                            >
                                                {agent.full_name}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
                
                <DropdownMenu>
                    <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                        <Button variant="ghost" size="icon" className="h-6 w-6">
                            <MoreVertical className="h-4 w-4" />
                        </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onView(lead); }}>
                            View Details
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onSetReminder(lead); }}>
                            <Bell className="h-4 w-4 mr-2" />
                            Set Reminder
                        </DropdownMenuItem>
                    </DropdownMenuContent>
                </DropdownMenu>
            </div>
            
            <div className="space-y-2 text-sm">
                <div className="flex items-center gap-2 text-muted-foreground">
                    <Phone className="h-3 w-3" />
                    <span className="font-mono flex-1">{lead.phone}</span>
                    <ClickToCall 
                        phoneNumber={lead.phone} 
                        contactId={lead.id} 
                        contactName={lead.full_name}
                        size="sm"
                        className="h-6 w-6"
                    />
                </div>
                {(() => {
                    // Dedicated, always-visible secondary phone row — visually
                    // separated from the primary so notes/activity below it
                    // cannot "bury" it.  Maps directly to lead.secondary_phone
                    // (not to a "Secondary Phone:" line inside notes, which
                    // agents kept overwriting).
                    const sec = effectiveSecondary.trim();
                    if (!sec) {
                        return (
                            <InlineSecondaryPhoneEditor
                                leadId={lead.id}
                                value=""
                                primaryPhone={lead.phone}
                                mode="add"
                                onUpdated={(v) => setSecondaryOverride(v)}
                            />
                        );
                    }
                    return (
                        <div
                            className="flex items-center gap-2 px-2 py-1 rounded-md bg-amber-50 border border-amber-200"
                            data-testid={`lead-card-secondary-${lead.id}`}
                        >
                            <Phone className="h-3 w-3 text-amber-600 shrink-0" />
                            <span className="text-[9px] font-bold uppercase tracking-wide text-amber-700 bg-amber-100 rounded px-1 py-0.5">
                                2nd
                            </span>
                            <span className="font-mono flex-1 text-xs text-amber-900 truncate">
                                {sec}
                            </span>
                            <InlineSecondaryPhoneEditor
                                leadId={lead.id}
                                value={sec}
                                primaryPhone={lead.phone}
                                mode="edit"
                                onUpdated={(v) => setSecondaryOverride(v)}
                            />
                            <ClickToCall
                                phoneNumber={sec}
                                contactId={lead.id}
                                contactName={lead.full_name}
                                size="sm"
                                className="h-6 w-6"
                            />
                        </div>
                    );
                })()}
                <NumberConflictBadge lead={lead} compact />
                {lead.email && (
                    <p className="flex items-center gap-2 text-muted-foreground truncate">
                        <Mail className="h-3 w-3" />
                        {lead.email}
                    </p>
                )}
                {lead.country && (
                    <p className="flex items-center gap-2 text-muted-foreground">
                        <MapPin className="h-3 w-3" />
                        {lead.country}
                    </p>
                )}
            </div>
            
            {/* Course Interest & Estimated Value - Show for pipeline stages */}
            {(lead.interested_course_name || lead.estimated_value) && (
                <div className="mt-2 p-2 bg-primary/5 rounded-md border border-primary/10">
                    {lead.interested_course_name && (
                        <p className="text-xs font-medium text-primary truncate" title={lead.interested_course_name}>
                            {lead.interested_course_name}
                        </p>
                    )}
                    {lead.selected_addons?.length > 0 && (
                        <p className="text-xs text-muted-foreground truncate" title={lead.selected_addons.map(a => a.name?.replace('Addons - ', '')).join(', ')}>
                            + {lead.selected_addons.map(a => a.name?.replace('Addons - ', '')).join(', ')}
                        </p>
                    )}
                    {lead.estimated_value > 0 && (
                        <p className="text-sm font-bold text-emerald-600 mt-0.5">
                            {formatCurrency(lead.estimated_value)}
                        </p>
                    )}
                </div>
            )}
            
            {/* Show sale amount for enrolled leads */}
            {lead.stage === 'enrolled' && lead.sale_amount > 0 && (
                <div className="mt-2 p-2 bg-emerald-500/10 rounded-md border border-emerald-500/20">
                    <p className="text-xs text-emerald-600 font-semibold flex items-center gap-1"><CheckCircle className="h-3 w-3" /> Enrolled</p>
                    <p className="text-sm font-bold text-emerald-600">
                        {formatCurrency(lead.sale_amount)}
                    </p>
                </div>
            )}

            {/* Show rejection reason for rejected leads */}
            {lead.stage === 'rejected' && (
                <div className="mt-2 p-2 bg-rose-500/10 rounded-md border border-rose-500/20">
                    <p className="text-xs text-rose-600 font-semibold flex items-center gap-1"><AlertTriangle className="h-3 w-3" /> Rejected</p>
                    {lead.rejection_reason && <p className="text-xs text-rose-500 mt-0.5">{lead.rejection_reason}</p>}
                </div>
            )}
            
            <div className="flex items-center justify-between mt-3 pt-3 border-t border-border">
                <span className="text-xs text-muted-foreground flex items-center gap-1">
                    <Clock className="h-3 w-3" />
                    {formatDate(lead.created_at)}
                </span>
                <LeadOriginStamp lead={lead} compact />
                <div className="flex items-center gap-1 flex-wrap justify-end">
                    <LastActivityBadge at={lead.last_activity} by={lead.last_activity_by} stage={lead.stage} compact testId={`lead-activity-${lead.id}`} />
                    {lead.follow_up_date && (
                        <Badge variant="outline" className="text-xs text-blue-600 border-blue-300 bg-blue-50">
                            <CalendarDays className="h-3 w-3 mr-1" />
                            {new Date(lead.follow_up_date).toLocaleDateString('en-AE', { day: 'numeric', month: 'short' })}
                        </Badge>
                    )}
                    {hasReminder && (
                        <Badge className="bg-amber-500 text-white text-xs">
                            <Bell className="h-3 w-3 mr-1" />
                            {lead.reminder_time || 'Set'}
                        </Badge>
                    )}
                    {Array.isArray(lead.tags) && lead.tags.includes('Reassigned Lead') && (
                        <Badge
                            className="bg-violet-600 text-white text-xs hover:bg-violet-600"
                            title="This lead was reassigned via the ERP"
                            data-testid={`lead-tag-reassigned-${lead.id}`}
                        >
                            Reassigned
                        </Badge>
                    )}
                    {lead.sla_breach && lead.stage !== 'new_lead' && (
                        <Badge className="bg-red-500 text-white text-xs">
                            <AlertTriangle className="h-3 w-3 mr-1" />
                            SLA
                        </Badge>
                    )}
                    <SlaCountdownChip lead={lead} compact />
                </div>
            </div>
        </div>
    );
};

// Sortable wrapper for LeadCard
const SortableLeadCard = ({ lead, onUpdate, onView, onSetReminder, isSuperAdmin, availableAgents, onQuickReassign }) => {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id: lead.id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
    };

    return (
        <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
            <LeadCard
                lead={lead}
                onUpdate={onUpdate}
                onView={onView}
                onSetReminder={onSetReminder}
                isDragging={isDragging}
                isSuperAdmin={isSuperAdmin}
                availableAgents={availableAgents}
                onQuickReassign={onQuickReassign}
            />
        </div>
    );
};

const KanbanColumn = ({ stage, stageData, onUpdate, onView, onSetReminder, isSuperAdmin, availableAgents, onQuickReassign, onPageChange }) => {
    const items = stageData?.items || [];
    const total = stageData?.total || 0;
    const page = stageData?.page || 1;
    const totalPages = stageData?.total_pages || 1;
    const leadIds = items.map(l => l.id);
    
    const { setNodeRef, isOver } = useDroppable({ id: stage.id });
    
    return (
        <div 
            ref={setNodeRef}
            className={`kanban-column ${isOver ? 'ring-2 ring-primary ring-offset-2' : ''}`} 
            data-testid={`kanban-column-${stage.id}`} 
            data-stage={stage.id}
        >
            <div className="kanban-column-header">
                <div className="flex items-center gap-2">
                    <div className={`w-3 h-3 rounded-full ${stage.color}`} />
                    <h3 className="font-semibold">{stage.label}</h3>
                </div>
                <Badge variant="secondary" data-testid={`stage-count-${stage.id}`}>{total}</Badge>
            </div>
            
            <ScrollArea className="flex-1">
                <SortableContext items={leadIds} strategy={verticalListSortingStrategy}>
                    <div className="space-y-3 min-h-[100px] p-1">
                        {items.map((lead) => (
                            <SortableLeadCard
                                key={lead.id}
                                lead={lead}
                                onUpdate={onUpdate}
                                onView={onView}
                                onSetReminder={onSetReminder}
                                isSuperAdmin={isSuperAdmin}
                                availableAgents={availableAgents}
                                onQuickReassign={onQuickReassign}
                            />
                        ))}
                        {items.length === 0 && (
                            <div className={`text-center text-muted-foreground py-8 text-sm border-2 border-dashed rounded-lg transition-colors ${isOver ? 'border-primary bg-primary/5' : 'border-muted'}`}>
                                Drop leads here
                            </div>
                        )}
                    </div>
                </SortableContext>
            </ScrollArea>
            {totalPages > 1 && (
                <div className="flex items-center justify-center gap-1 pt-2 border-t border-border">
                    <button onClick={() => onPageChange(stage.id, page - 1)} disabled={page <= 1}
                        className="px-2 py-0.5 text-xs rounded hover:bg-muted disabled:opacity-30">Prev</button>
                    <span className="text-xs text-muted-foreground">{page}/{totalPages}</span>
                    <button onClick={() => onPageChange(stage.id, page + 1)} disabled={page >= totalPages}
                        className="px-2 py-0.5 text-xs rounded hover:bg-muted disabled:opacity-30">Next</button>
                </div>
            )}
        </div>
    );
};

const SalesCRMPage = () => {
    const { user } = useAuth();
    const [leads, setLeads] = useState([]);
    const [kanbanData, setKanbanData] = useState({});
    const [kanbanPages, setKanbanPages] = useState({});
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showDetailModal, setShowDetailModal] = useState(false);
    const [showReminderModal, setShowReminderModal] = useState(false);
    const [showEnrollmentModal, setShowEnrollmentModal] = useState(false);
    const [showRejectionModal, setShowRejectionModal] = useState(false);
    const [showReactivateModal, setShowReactivateModal] = useState(false);
    const [pendingEnrollmentLead, setPendingEnrollmentLead] = useState(null);
    const [pendingRejectionLead, setPendingRejectionLead] = useState(null);
    const [pendingReactivateLead, setPendingReactivateLead] = useState(null);
    const [pendingReactivateStage, setPendingReactivateStage] = useState(null);
    const [reminderLead, setReminderLead] = useState(null);
    const [selectedLead, setSelectedLead] = useState(null);
    // Fetch last-pickup map for the open lead — powers green "Picked up"
    // badges next to whichever number the customer actually answered.
    const selectedPickup = useLastPickup(selectedLead?.id, !!(showDetailModal && selectedLead?.id));
    const [activeId, setActiveId] = useState(null);
    const [formData, setFormData] = useState({
        full_name: '',
        phone: '',
        email: '',
        country: '',
        city: '',
        lead_source: '',
        course_of_interest: '',
        notes: '',
        assigned_to: '',
    });
    const [updateData, setUpdateData] = useState({
        stage: '',
        call_notes: '',
        rejection_reason: '',
        follow_up_date: '',
        interested_course_id: '',
        estimated_value: '',
        selectedAddons: [],
    });
    const [courses, setCourses] = useState([]);
    const [catalogCourses, setCatalogCourses] = useState([]);
    const [catalogAddons, setCatalogAddons] = useState([]);
    const [duplicateInfo, setDuplicateInfo] = useState(null);
    const [showDuplicateDialog, setShowDuplicateDialog] = useState(false);
    const [merging, setMerging] = useState(false);
    const [availableAgents, setAvailableAgents] = useState([]);
    const [agentLeadCounts, setAgentLeadCounts] = useState({});
    const [teams, setTeams] = useState([]);
    const [filterAgent, setFilterAgent] = useState('all');
    const [filterTeam, setFilterTeam] = useState('all');
    const [filterStage, setFilterStage] = useState('all');
    const [filterReassignedOnly, setFilterReassignedOnly] = useState(false);
    const [deepSearchResults, setDeepSearchResults] = useState(null);
    const [deepSearching, setDeepSearching] = useState(false);
    const [periodFilter, setPeriodFilter] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(50);
    const [totalRecords, setTotalRecords] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    // Lead notes history
    const [leadNotes, setLeadNotes] = useState([]);
    const [newNote, setNewNote] = useState('');
    const [noteSaving, setNoteSaving] = useState(false);
    const [notesLoading, setNotesLoading] = useState(false);

    const [editingName, setEditingName] = useState(false);
    const [editNameValue, setEditNameValue] = useState('');

    const canEditName = selectedLead && !['enrolled', 'rejected'].includes(selectedLead.stage);

    const handleSaveName = async () => {
        const trimmed = editNameValue.trim();
        if (!trimmed || trimmed.length < 2 || trimmed === selectedLead.full_name) {
            setEditingName(false);
            return;
        }
        try {
            await leadApi.update(selectedLead.id, { full_name: trimmed });
            toast.success('Name updated');
            setSelectedLead(prev => ({ ...prev, full_name: trimmed }));
            setEditingName(false);
            fetchLeads();
        } catch (err) {
            toast.error(err?.response?.data?.detail || 'Failed to update name');
        }
    };

    const isSuperAdmin = user?.role === 'super_admin';
    // `isTeamLeader` is misnamed — it means "user has a team view of some
    // shape" (TL / SM / MoA). The strict-TL check below uses `isStrictTL`
    // so we don't accidentally restrict SMs to a single team.
    const isTeamLeader = ['team_leader', 'sales_manager', 'master_of_academics'].includes(user?.role);
    const isStrictTL = user?.role === 'team_leader';
    const isHeadOrAdmin = isSuperAdmin || isTeamLeader || user?.role === 'admin';
    // Filters that show every team's data should not be visible to TLs —
    // they're scoped to their own team via the backend role-based query anyway,
    // and giving them a Team dropdown listing other teams creates a UX with
    // no useful filter values for them. Restrict to managers and admins.
    const canFilterByTeam = ['sales_manager', 'admin', 'super_admin'].includes(user?.role);
    // Enrolled column visibility: hidden from sales executives only
    // (team members). Team leaders, sales managers, admins, super admins,
    // and every other role land in the Sales CRM with the Enrolled column
    // visible. Sales executives can still enroll a lead via the lead-
    // detail stage dropdown — only the perpetually-filled column is
    // removed from their pipeline view.
    const canSeeEnrolledColumn = user?.role !== 'sales_executive';
    // Rejected column visibility: Sales Manager / CEO / COO only.
    const canSeeRejectedColumn = ['super_admin', 'admin', 'coo', 'sales_manager'].includes(user?.role);
    const visibleLeadStages = [
        ...(canSeeEnrolledColumn
            ? LEAD_STAGES
            : LEAD_STAGES.filter(s => s.id !== 'enrolled')),
        ...(canSeeRejectedColumn ? [REJECTED_STAGE] : []),
    ];
    const [viewMode, setViewMode] = useState(isHeadOrAdmin ? 'team' : 'my_leads');
    const [displayMode, setDisplayMode] = useState('kanban'); // 'kanban' or 'table'

    // Pipeline stages that require course selection
    const PIPELINE_STAGES = ['warm_lead', 'hot_lead'];

    // Drag and drop sensors
    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 8,
            },
        }),
        useSensor(KeyboardSensor)
    );

    // Use a ref-based request counter to ignore stale fetch responses.
    // Without this, a race between the initial mount fetch (with empty
    // periodFilter) and the periodFilter-applied fetch makes the older
    // unfiltered response win, leaving pagination indicators and data
    // out of sync (e.g. showing "1/41" for call_back while the active
    // filter only has 4 items — so clicking Next returns an empty page).
    const fetchReqIdRef = React.useRef(0);

    useEffect(() => {
        fetchLeads();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPage, pageSize, displayMode]);

    // When a filter changes, reset kanban pagination to page 1 so the
    // "X/Y" indicator matches the freshly-filtered data. Also bumps the
    // request id so any in-flight unfiltered fetch is discarded on arrival.
    useEffect(() => {
        setKanbanPages({});
        fetchLeads({});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filterAgent, filterTeam, filterStage, filterReassignedOnly, periodFilter, viewMode]);

    const handleKanbanPageChange = (stageId, newPage) => {
        const updated = { ...kanbanPages, [stageId]: newPage };
        setKanbanPages(updated);
        fetchLeads(updated);
    };

    // Refresh the per-agent lead-count map shown in the agent filter
    // dropdown. Extracted so we can call it after every stage change /
    // rejection / enrollment so the badge stays in sync with the kanban.
    const refreshAgentLeadCounts = React.useCallback(async () => {
        if (!isHeadOrAdmin) return;
        if (!(canFilterByTeam || isStrictTL)) return;
        try {
            const res = await apiClient.get('/sales/agent-lead-counts');
            const countMap = {};
            for (const row of (res?.data || [])) {
                countMap[row.agent_id] = { lead_count: row.lead_count, enrolled_count: row.enrolled_count };
            }
            setAgentLeadCounts(countMap);
        } catch (e) {
            // silent — old counts stay visible until the next refresh
        }
    }, [isHeadOrAdmin, canFilterByTeam, isStrictTL]);

    // Fetch agents and teams for admin/team leaders
    useEffect(() => {
        if (isHeadOrAdmin) {
            const fetchAgentsAndTeams = async () => {
                try {
                    const requests = [
                        apiClient.get('/users?roles=sales_executive,team_leader,sales_manager,mentor,master_of_academics'),
                        apiClient.get('/teams?department=Sales'),
                    ];
                    // Fetch lead counts for managers AND team leaders.
                    // TLs see counts next to their own team members; SMs/admins
                    // see counts across everyone. The backend scopes TL → own
                    // team automatically.
                    if (canFilterByTeam || isStrictTL) {
                        requests.push(apiClient.get('/sales/agent-lead-counts').catch(() => ({ data: [] })));
                    }
                    const [agentsRes, teamsRes, countsRes] = await Promise.all(requests);
                    let agents = (agentsRes.data || []).filter(u => u.is_active);
                    // STRICT TL → restrict the "All Agents" dropdown to
                    // only their own team's members. SM / MoA keep full
                    // visibility (they can narrow via the team filter).
                    // Match by EITHER team_id OR team_leader_id pointing to
                    // the TL — some agents are linked to a team-leader
                    // explicitly while sitting in a different team_id
                    // (e.g., reassigned executives kept their old team_id).
                    // Backend `/api/leads` already uses the same OR clause,
                    // so without this OR the agent dropdown showed fewer
                    // people than the actual lead list — agents like Rukkiya
                    // (team_leader_id = Aleesha but team_id = another team)
                    // disappeared.
                    if (isStrictTL && user?.id) {
                        agents = agents.filter(a =>
                            a.team_leader_id === user.id
                            || (user.team_id && a.team_id === user.team_id)
                        );
                    }
                    // Always add TL themselves to the list so they can self-assign
                    if (isStrictTL && user?.id) {
                        if (!agents.find(a => a.id === user.id)) {
                            agents.unshift({ id: user.id, full_name: (user.full_name || user.name) + ' (Me)', team_id: user.team_id, team_name: user.team_name || '', role: user.role });
                        }
                    }
                    // Sort: TLs/managers first, then sales execs, then mentors
                    const roleOrder = { team_leader: 0, sales_manager: 0, master_of_academics: 0, master_of_academics_: 0, sales_executive: 1, mentor: 2 };
                    agents.sort((a, b) => (roleOrder[a.role] ?? 1) - (roleOrder[b.role] ?? 1));
                    setAvailableAgents(agents);
                    setTeams(teamsRes.data || []);
                    const countMap = {};
                    for (const row of (countsRes?.data || [])) {
                        countMap[row.agent_id] = { lead_count: row.lead_count, enrolled_count: row.enrolled_count };
                    }
                    setAgentLeadCounts(countMap);
                } catch (e) {
                    logger.error('Failed to fetch agents/teams:', e);
                }
            };
            fetchAgentsAndTeams();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isHeadOrAdmin, isTeamLeader, isStrictTL, isSuperAdmin, canFilterByTeam, user?.team_id]);

    // Fetch course catalog for course/addon selection
    useEffect(() => {
        const fetchCatalog = async () => {
            try {
                const res = await apiClient.get('/course-catalog');
                const grouped = res.data?.grouped || {};
                setCatalogCourses(grouped.courses || []);
                setCatalogAddons(grouped.addons || []);
                // Keep legacy courses for backward compat
                setCourses(res.data?.items || []);
            } catch (error) {
                logger.error('Failed to fetch course catalog:', error);
                // Fallback to legacy courses endpoint
                try {
                    const res = await apiClient.get('/courses');
                    setCourses(res.data || []);
                } catch (e) {
                    logger.error('Failed to fetch courses:', e);
                }
            }
        };
        fetchCatalog();
    }, []);

    // Auto-detect country when phone number changes
    const handlePhoneChange = (phone) => {
        setFormData(prev => {
            const detectedCountry = detectCountryFromPhone(phone);
            return {
                ...prev,
                phone,
                country: detectedCountry || prev.country,
            };
        });
    };

    const fetchLeads = async (kanbanPageOverrides) => {
        // Increment request id; any in-flight older request will discard its response.
        const reqId = ++fetchReqIdRef.current;
        try {
            setLoading(true);
            
            if (displayMode === 'kanban') {
                // Use kanban endpoint for per-stage pagination
                const params = new URLSearchParams();
                if (searchTerm) params.set('search', searchTerm);
                if (viewMode === 'my_leads') params.set('assigned_to', user.id);
                if (filterAgent !== 'all') params.set('assigned_to', filterAgent);
                if (filterTeam !== 'all') params.set('team_id', filterTeam);
                if (periodFilter?.date_from) params.set('date_from', periodFilter.date_from);
                if (periodFilter?.date_to) params.set('date_to', periodFilter.date_to);
                if (periodFilter?.date_field) params.set('date_field', periodFilter.date_field);
                if (filterReassignedOnly) params.set('tag', 'Reassigned Lead');
                params.set('per_stage', '20');
                // Per-stage pages
                const pages = kanbanPageOverrides || kanbanPages;
                if (pages.new_lead) params.set('page_new_lead', pages.new_lead);
                if (pages.call_back) params.set('page_call_back', pages.call_back);
                if (pages.warm_lead) params.set('page_warm_lead', pages.warm_lead);
                if (pages.hot_lead) params.set('page_hot_lead', pages.hot_lead);
                if (pages.enrolled) params.set('page_enrolled', pages.enrolled);
                
                const response = await apiClient.get(`/leads/kanban?${params}`);
                // Discard stale response (a newer fetch has superseded this one)
                if (reqId !== fetchReqIdRef.current) return;
                const data = response.data;
                setKanbanData(data.stages || {});

                // Safety net: if we requested a page beyond what's available
                // (e.g. lead got deleted/moved after we loaded the prior page),
                // clamp the page number back to the last valid page so the
                // pagination indicator and items stay in sync.
                const clamped = {};
                for (const [stageName, sd] of Object.entries(data.stages || {})) {
                    const tp = sd.total_pages || 1;
                    if (sd.page > tp) clamped[stageName] = tp;
                }
                if (Object.keys(clamped).length > 0) {
                    setKanbanPages(prev => ({ ...prev, ...clamped }));
                }

                // Flatten all items for drag-and-drop context
                const allItems = [];
                for (const sd of Object.values(data.stages || {})) {
                    allItems.push(...(sd.items || []));
                }
                setLeads(allItems);
                setTotalRecords(Object.values(data.stage_counts || {}).reduce((s, c) => s + c, 0));
            } else {
                // Table mode: use regular paginated endpoint
                const params = { search: searchTerm || undefined, page: currentPage, page_size: pageSize };
                if (viewMode === 'my_leads') params.assigned_to = user.id;
                if (filterAgent !== 'all') params.assigned_to = filterAgent;
                if (filterTeam !== 'all') params.team_id = filterTeam;
                if (filterStage !== 'all') {
                    params.stage = filterStage;
                } else {
                    // Match the Kanban view: rejected / not_interested are visualised
                    // in their own columns, not mixed with the active pipeline.
                    // The table view should follow the same convention by default.
                    params.exclude_rejected = true;
                }
                if (filterReassignedOnly) params.tag = 'Reassigned Lead';
                if (periodFilter) {
                    params.date_from = periodFilter.date_from;
                    params.date_to = periodFilter.date_to;
                    params.date_field = periodFilter.date_field;
                }
                const response = await leadApi.getAll(params);
                if (reqId !== fetchReqIdRef.current) return;
                const data = response.data;
                if (data && data.items) {
                    setLeads(data.items);
                    setTotalRecords(data.total || 0);
                    setTotalPages(data.total_pages || 0);
                } else {
                    setLeads(Array.isArray(data) ? data : []);
                }
            }
        } catch (error) {
            if (reqId === fetchReqIdRef.current) {
                toast.error('Failed to fetch leads');
                logger.error(error);
            }
        } finally {
            if (reqId === fetchReqIdRef.current) setLoading(false);
            // Keep the agent-lead-count badge in sync after every fetch
            // (drag-drop moves, rejections, enrollments, and removals all
            // trigger fetchLeads → here).
            refreshAgentLeadCounts();
        }
    };

    const handleSearch = (e) => {
        setSearchTerm(e.target.value);
        setDeepSearchResults(null);
        // Debounce search
        setTimeout(() => fetchLeads(), 500);
    };

    const handleDeepSearch = async () => {
        if (!searchTerm.trim()) return;
        setDeepSearching(true);
        try {
            const response = await leadApi.getAll({ search: searchTerm.trim() });
            const data = response.data;
            setDeepSearchResults(data?.items || (Array.isArray(data) ? data : []));
        } catch (error) {
            toast.error('Deep search failed');
        } finally {
            setDeepSearching(false);
        }
    };

    // Drag and drop handlers
    const handleDragStart = (event) => {
        setActiveId(event.active.id);
    };

    const handleDragEnd = async (event) => {
        const { active, over } = event;
        setActiveId(null);

        if (!over) return;

        const activeLeadId = active.id;
        const activeLead = leads.find(l => l.id === activeLeadId);
        
        if (!activeLead) return;

        // Determine target stage
        let targetStage = null;
        
        // Check if dropped over a column (stage id)
        const isColumnDrop = LEAD_STAGES.some(s => s.id === over.id);
        if (isColumnDrop) {
            targetStage = over.id;
        } else {
            // Dropped over another lead - get that lead's stage
            const overLead = leads.find(l => l.id === over.id);
            if (overLead) {
                targetStage = overLead.stage;
            }
        }

        // If dropped in a different stage, update the lead
        if (targetStage && targetStage !== activeLead.stage) {
            // REACTIVATE: rejected/not_interested → active. Only super_admin
            // and sales_manager may revive rejected leads. Force them
            // through the reassignment modal so a new owner is picked
            // atomically with the stage change.
            const isReviving = ['rejected', 'not_interested'].includes(activeLead.stage) &&
                               !['rejected', 'not_interested'].includes(targetStage);
            if (isReviving) {
                if (!['super_admin', 'sales_manager'].includes(user?.role)) {
                    toast.error('Only Super Admin or Sales Manager can reactivate rejected leads');
                    return;
                }
                setPendingReactivateLead(activeLead);
                setPendingReactivateStage(targetStage);
                setShowReactivateModal(true);
                return;
            }

            // Check if moving to enrolled - show payment modal
            if (targetStage === 'enrolled') {
                // Check if course is selected
                if (!activeLead.interested_course_id) {
                    toast.error('Please select a course of interest before enrolling');
                    return;
                }
                setPendingEnrollmentLead(activeLead);
                setShowEnrollmentModal(true);
                return;
            }
            
            // Check if moving to rejected - show rejection modal
            if (targetStage === 'rejected') {
                setPendingRejectionLead(activeLead);
                setShowRejectionModal(true);
                return;
            }
            
            try {
                await leadApi.update(activeLeadId, { stage: targetStage });
                toast.success(`Lead moved to ${LEAD_STAGES.find(s => s.id === targetStage)?.label || targetStage}`);
                fetchLeads();
            } catch (error) {
                const detail = error.response?.data?.detail || 'Failed to move lead';
                if (detail.includes('course') || detail.includes('Course')) {
                    toast.error('Please open the lead details and select a course + add-ons first');
                } else {
                    toast.error(detail);
                }
                logger.error(error);
            }
        }
    };

    // Handle enrollment with payment details
    const [enrolling, setEnrolling] = useState(false);
    
    const handleEnrollmentComplete = async (paymentData) => {
        if (!pendingEnrollmentLead || enrolling) return;
        
        setEnrolling(true);
        try {
            // Get course details
            const courseId = pendingEnrollmentLead.interested_course_id;
            const selectedCourse = catalogCourses.find(c => c.id === courseId) || courses.find(c => c.id === courseId);
            
            const updatePayload = {
                stage: 'enrolled',
                // Course info
                interested_course_id: courseId,
                interested_course_name: pendingEnrollmentLead.interested_course_name || selectedCourse?.name,
                course_id: courseId,
                course_name: pendingEnrollmentLead.interested_course_name || selectedCourse?.name,
                // Payment info
                payment_method: paymentData.payment_method,
                payment_amount: paymentData.payment_amount,
                payment_date: paymentData.payment_date,
                payment_proof: paymentData.payment_proof,
                payment_proof_filename: paymentData.payment_proof_filename,
                transaction_id: paymentData.transaction_id,
                payment_notes: paymentData.payment_notes,
                sale_amount: paymentData.payment_amount,
                // Split payment support
                is_split_payment: paymentData.is_split_payment,
                payment_splits: paymentData.payment_splits,
                // BNPL phone verification
                bnpl_phone: paymentData.bnpl_phone,
                bnpl_same_number: paymentData.bnpl_same_number,
            };
            
            await leadApi.update(pendingEnrollmentLead.id, updatePayload);
            toast.success('Lead enrolled successfully! Sent to Finance for verification.');
            setShowEnrollmentModal(false);
            setPendingEnrollmentLead(null);
            fetchLeads();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to enroll lead');
        } finally {
            setEnrolling(false);
        }
    };

    // Handle rejection with reason
    const handleRejectionComplete = async (rejectionData) => {
        if (!pendingRejectionLead) return;
        
        try {
            await leadApi.update(pendingRejectionLead.id, {
                stage: 'rejected',
                rejection_reason: rejectionData.rejection_reason,
                rejection_reason_label: rejectionData.rejection_reason_label,
                rejection_notes: rejectionData.rejection_notes,
            });
            toast.success('Lead marked as rejected');
            setShowRejectionModal(false);
            setPendingRejectionLead(null);
            fetchLeads();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to reject lead');
        }
    };

    const handleCreateLead = async (e) => {
        e.preventDefault();
        
        if (!formData.full_name || !formData.phone) {
            toast.error('Name and phone are required');
            return;
        }
        
        try {
            const payload = { ...formData };
            if (!payload.assigned_to || payload.assigned_to === 'round_robin') delete payload.assigned_to;
            await leadApi.create(payload);
            toast.success('Lead created successfully');
            setShowCreateModal(false);
            setFormData({
                full_name: '',
                phone: '',
                email: '',
                country: '',
                city: '',
                lead_source: '',
                course_of_interest: '',
                notes: '',
                assigned_to: '',
            });
            fetchLeads();
        } catch (error) {
            if (error.response?.status === 409 && error.response?.data?.duplicate) {
                setDuplicateInfo(error.response.data);
                setShowCreateModal(false);
                setShowDuplicateDialog(true);
            } else {
                toast.error(error.response?.data?.detail || 'Failed to create lead');
            }
        }
    };

    const handleMerge = async () => {
        if (!duplicateInfo?.existing_lead?.id) return;
        setMerging(true);
        try {
            await apiClient.post(`/leads/${duplicateInfo.existing_lead.id}/merge`, formData);
            toast.success('Lead merged successfully — missing fields updated');
            setShowDuplicateDialog(false);
            setDuplicateInfo(null);
            setFormData({ full_name: '', phone: '', email: '', country: '', city: '', lead_source: '', course_of_interest: '', notes: '', assigned_to: '' });
            fetchLeads();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Merge failed');
        }
        setMerging(false);
    };

    const handleUpdateLead = async () => {
        if (!selectedLead) return;
        
        const updates = {};
        if (updateData.stage) updates.stage = updateData.stage;
        if (updateData.rejection_reason) updates.rejection_reason = updateData.rejection_reason;
        if (updateData.follow_up_date) updates.follow_up_date = updateData.follow_up_date;
        
        // Add course interest for pipeline stages
        if (updateData.interested_course_id) {
            updates.interested_course_id = updateData.interested_course_id;
            const selectedCourse = catalogCourses.find(c => c.id === updateData.interested_course_id)
                || courses.find(c => c.id === updateData.interested_course_id);
            if (selectedCourse) {
                updates.interested_course_name = selectedCourse.name;
                const coursePrice = selectedCourse.price || selectedCourse.base_price || 0;
                const addonsPrice = (updateData.selectedAddons || []).reduce((sum, a) => sum + (a.price || 0), 0);
                updates.course_value = coursePrice;
                updates.addons_value = addonsPrice;
                if (!updateData.estimated_value) {
                    updates.estimated_value = coursePrice + addonsPrice;
                }
            }
        }
        if (updateData.selectedAddons?.length > 0) {
            updates.selected_addons = updateData.selectedAddons;
        }
        if (updateData.estimated_value) {
            updates.estimated_value = parseFloat(updateData.estimated_value);
        }
        
        // Check if changing to enrolled - show payment modal
        if (updates.stage === 'enrolled' && selectedLead.stage !== 'enrolled') {
            const courseId = updates.interested_course_id || selectedLead.interested_course_id;
            if (!courseId) {
                toast.error('Please select a course of interest before enrolling');
                return;
            }
            setShowDetailModal(false);
            setPendingEnrollmentLead({...selectedLead, ...updates});
            setShowEnrollmentModal(true);
            return;
        }
        
        // Check if changing to rejected - show rejection modal
        if (updates.stage === 'rejected' && selectedLead.stage !== 'rejected') {
            setShowDetailModal(false);
            setPendingRejectionLead(selectedLead);
            setShowRejectionModal(true);
            return;
        }
        
        // Validate course selection for pipeline stages
        if (PIPELINE_STAGES.includes(updates.stage)) {
            const courseId = updates.interested_course_id || selectedLead.interested_course_id;
            if (!courseId) {
                toast.error('Please select which course/package the client is interested in');
                return;
            }
        }
        
        try {
            await leadApi.update(selectedLead.id, updates);
            toast.success('Lead updated successfully');
            setShowDetailModal(false);
            setSelectedLead(null);
            setUpdateData({ stage: '', call_notes: '', rejection_reason: '', follow_up_date: '', interested_course_id: '', estimated_value: '', selectedAddons: [] });
            fetchLeads();
        } catch (error) {
            toast.error(apiErrorMessage(error, 'Failed to update lead'), { duration: 8000 });
        }
    };

    const handleViewLead = (lead) => {
        setSelectedLead(lead);
        setUpdateData({
            stage: lead.stage,
            call_notes: '',
            rejection_reason: lead.rejection_reason || '',
            follow_up_date: '',
            interested_course_id: lead.interested_course_id || '',
            estimated_value: lead.estimated_value || '',
            selectedAddons: lead.selected_addons || [],
        });
        // Reset per-lead UI state so drafts/notes from a previously-opened
        // lead never leak into the next lead. Without this the Notes History
        // textarea keeps showing the previous lead's draft, and the user can
        // accidentally save the same note to multiple unrelated leads.
        setNewNote('');
        setLeadNotes([]);
        setShowDetailModal(true);
        fetchLeadNotes(lead.id);
    };

    const fetchLeadNotes = async (leadId) => {
        setNotesLoading(true);
        try {
            const res = await apiClient.get(`/leads/${leadId}/notes`);
            setLeadNotes(res.data || []);
        } catch {
            setLeadNotes([]);
        }
        setNotesLoading(false);
    };

    const handleAddNote = async () => {
        if (!newNote.trim() || !selectedLead) return;
        // Capture the lead id at the moment of submission so a slow network
        // call cannot land on a different lead the user has since opened.
        const targetLeadId = selectedLead.id;
        const noteText = newNote.trim();
        setNoteSaving(true);
        try {
            const res = await apiClient.post(`/leads/${targetLeadId}/notes`, {
                text: noteText,
                type: 'call_note',
            });
            // Only update the displayed list if the user is still viewing the
            // same lead — otherwise we'd polluate the wrong lead's UI.
            if (selectedLead?.id === targetLeadId) {
                setLeadNotes(prev => [res.data, ...prev]);
                setNewNote('');
            }
            toast.success('Note saved');
        } catch {
            toast.error('Failed to save note');
        }
        setNoteSaving(false);
    };

    const handleSetReminder = (lead) => {
        setReminderLead(lead);
        setShowReminderModal(true);
    };

    const handleQuickReassign = async (lead, agent) => {
        try {
            await apiClient.put(`/leads/${lead.id}`, {
                assigned_to: agent.id,
                assigned_to_name: agent.full_name,
            });
            toast.success(`Reassigned to ${agent.full_name}`);
            fetchLeads();
        } catch (e) {
            logger.error(e);
            toast.error('Failed to reassign');
        }
    };

    const handleReminderSuccess = () => {
        setShowReminderModal(false);
        setReminderLead(null);
        fetchLeads();
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return 'N/A';
        return new Date(dateStr).toLocaleDateString('en-AE', {
            day: 'numeric',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
    };

    return (
        <div className="space-y-6" data-testid="sales-crm-page">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div>
                    <h1 className="text-3xl font-bold tracking-tight">Sales CRM</h1>
                    <p className="text-muted-foreground">Manage your leads and sales pipeline</p>
                </div>
                <div className="flex items-center gap-3 flex-wrap">
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                            placeholder="Search leads..."
                            value={searchTerm}
                            onChange={handleSearch}
                            className="pl-9 w-64"
                            data-testid="search-leads"
                        />
                    </div>
                    {canFilterByTeam && teams.length > 0 && (
                        <Select value={filterTeam} onValueChange={(v) => { setFilterTeam(v); setFilterAgent('all'); }} data-testid="filter-team">
                            <SelectTrigger className="w-[160px] h-9 text-xs" data-testid="filter-team-trigger">
                                <SelectValue placeholder="All Teams" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">All Teams</SelectItem>
                                {teams.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    )}
                    {isHeadOrAdmin && viewMode === 'team' && availableAgents.length > 0 && (
                        <Select value={filterAgent} onValueChange={setFilterAgent} data-testid="filter-agent">
                            <SelectTrigger className="w-[200px] h-9 text-xs" data-testid="filter-agent-trigger">
                                <SelectValue placeholder="All Agents" />
                            </SelectTrigger>
                            <SelectContent className="max-h-[420px]">
                                <SelectItem value="all">All Agents</SelectItem>
                                {(filterTeam !== 'all' ? availableAgents.filter(a => a.team_id === filterTeam) : availableAgents).map(a => {
                                    const c = agentLeadCounts[a.id];
                                    return (
                                        <SelectItem
                                            key={a.id}
                                            value={a.id}
                                            data-testid={`filter-agent-option-${a.id}`}
                                        >
                                            {(canFilterByTeam || isStrictTL) && c ? (
                                                <span className="inline-flex items-center justify-between gap-3 w-full">
                                                    <span className="truncate">{a.full_name}</span>
                                                    <span className="ml-auto text-[10px] tabular-nums px-1.5 py-0.5 rounded bg-muted text-muted-foreground">
                                                        {c.lead_count}
                                                    </span>
                                                </span>
                                            ) : (
                                                <span className="truncate">{a.full_name}</span>
                                            )}
                                        </SelectItem>
                                    );
                                })}
                            </SelectContent>
                        </Select>
                    )}
                    <Select value={filterStage} onValueChange={setFilterStage} data-testid="filter-stage">
                        <SelectTrigger className="w-[150px] h-9 text-xs" data-testid="filter-stage-trigger">
                            <SelectValue placeholder="All Stages" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All Stages</SelectItem>
                            {ALL_LEAD_STAGES.filter(s => s.id !== 'rejected' || canSeeRejectedColumn).map(s => <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>)}
                        </SelectContent>
                    </Select>
                    <button
                        type="button"
                        onClick={() => setFilterReassignedOnly(v => !v)}
                        title="Show only leads that were reassigned via the ERP"
                        data-testid="filter-reassigned-only"
                        className={`inline-flex items-center gap-1.5 h-9 px-3 rounded-md border text-xs font-medium transition-colors ${
                            filterReassignedOnly
                                ? 'bg-violet-600 text-white border-violet-600 hover:bg-violet-700'
                                : 'bg-background text-muted-foreground border-input hover:bg-accent'
                        }`}
                    >
                        <span className={`w-2 h-2 rounded-full ${filterReassignedOnly ? 'bg-white' : 'bg-violet-500'}`} />
                        Reassigned only
                    </button>
                    <PeriodFilter
                        onChange={(f) => setPeriodFilter(f ? {...f, date_field: 'any'} : null)}
                        defaultPeriod={null}
                    />
                    {['super_admin', 'admin', 'sales_manager', 'team_leader'].includes(user?.role) && (
                        <>
                            <ImportButton templateType="leads" title="Import Leads" onSuccess={fetchLeads} />
                            <Button onClick={() => setShowCreateModal(true)} data-testid="create-lead-btn">
                                <Plus className="h-4 w-4 mr-2" />
                                Add Lead
                            </Button>
                        </>
                    )}
                </div>
            </div>

            {/* View Mode Toggle for Team Leaders / Heads */}
            {isHeadOrAdmin && (
                <div className="flex items-center gap-3" data-testid="sales-view-toggle">
                    <div className="inline-flex rounded-lg border p-1 bg-muted/30">
                        <button
                            onClick={() => setViewMode('my_leads')}
                            className={`px-4 py-1.5 rounded-md text-sm font-medium transition-all ${viewMode === 'my_leads' ? 'bg-primary text-primary-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
                            data-testid="sales-view-my-leads"
                        >
                            My Leads
                        </button>
                        <button
                            onClick={() => setViewMode('team')}
                            className={`px-4 py-1.5 rounded-md text-sm font-medium transition-all ${viewMode === 'team' ? 'bg-primary text-primary-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
                            data-testid="sales-view-team"
                        >
                            Team Overview
                        </button>
                    </div>
                </div>
            )}

            {/* Kanban/Table Toggle */}
            <div className="flex items-center justify-between">
                <div className="inline-flex rounded-lg border p-1 bg-muted/30" data-testid="sales-display-toggle">
                    <button
                        onClick={() => setDisplayMode('kanban')}
                        className={`px-3 py-1 rounded-md text-sm font-medium transition-all flex items-center gap-1.5 ${displayMode === 'kanban' ? 'bg-primary text-primary-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
                        data-testid="sales-kanban-toggle"
                    >
                        <LayoutGrid className="h-3.5 w-3.5" />
                        Kanban
                    </button>
                    <button
                        onClick={() => setDisplayMode('table')}
                        className={`px-3 py-1 rounded-md text-sm font-medium transition-all flex items-center gap-1.5 ${displayMode === 'table' ? 'bg-primary text-primary-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
                        data-testid="sales-table-toggle"
                    >
                        <List className="h-3.5 w-3.5" />
                        Table
                    </button>
                </div>
            </div>

            {/* SLA Watchdog Panel — TL / SM / Admin only */}
            {['team_leader', 'sales_manager', 'admin', 'super_admin'].includes(user?.role) && (
                <SlaWatchdogPanel />
            )}

            {/* Kanban Board or Table View */}
            {loading ? (
                <div className="flex items-center justify-center h-96">
                    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
                </div>
            ) : displayMode === 'table' ? (
                /* Table View */
                <div className="border rounded-lg overflow-hidden" data-testid="sales-table-view">
                    <table className="w-full text-sm">
                        <thead className="bg-muted/50 border-b">
                            <tr>
                                <th className="text-left px-4 py-2.5 font-medium text-muted-foreground w-10">#</th>
                                <th className="text-left px-4 py-2.5 font-medium text-muted-foreground">Name</th>
                                <th className="text-left px-4 py-2.5 font-medium text-muted-foreground">Phone</th>
                                <th className="text-left px-4 py-2.5 font-medium text-muted-foreground">Stage</th>
                                <th className="text-left px-4 py-2.5 font-medium text-muted-foreground">Course Interest</th>
                                <th className="text-right px-4 py-2.5 font-medium text-muted-foreground">Amount</th>
                                <th className="text-left px-4 py-2.5 font-medium text-muted-foreground">Agent</th>
                                <th className="text-left px-4 py-2.5 font-medium text-muted-foreground">Assigned</th>
                                <th className="text-left px-4 py-2.5 font-medium text-muted-foreground">Updated</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y">
                            {leads.map((lead, idx) => {
                                const stageObj = LEAD_STAGES.find(s => s.id === lead.pipeline_stage);
                                // Resolve the most recent assignment timestamp.
                                // `assigned_at` is the canonical field, but some
                                // older reassignment paths only appended to
                                // assignment_history. Take whichever is more
                                // recent so the column always reflects reality.
                                const hist = lead.assignment_history || [];
                                const lastHist = hist.length ? hist[hist.length - 1] : null;
                                const lastHistDate = lastHist?.date || null;
                                let assignedTs = lead.assigned_at || lastHistDate;
                                if (lead.assigned_at && lastHistDate) {
                                    assignedTs = (new Date(lastHistDate) > new Date(lead.assigned_at))
                                        ? lastHistDate : lead.assigned_at;
                                }
                                return (
                                    <tr key={lead.id} className={`hover:bg-muted/30 cursor-pointer transition-colors ${lead.pipeline_stage === 'enrolled' ? 'bg-emerald-500/5 border-l-2 border-l-emerald-500' : lead.pipeline_stage === 'rejected' ? 'bg-rose-500/5 border-l-2 border-l-rose-500' : ''}`} onClick={() => handleViewLead(lead)} data-testid={`sales-table-row-${idx}`}>
                                        <td className="px-4 py-2.5 text-muted-foreground">{(currentPage - 1) * pageSize + idx + 1}</td>
                                        <td className="px-4 py-2.5 font-medium"><span className="inline-flex items-center gap-2">{lead.full_name || '—'}<NumberConflictBadge lead={lead} compact /></span></td>
                                        <td className="px-4 py-2.5 text-muted-foreground text-xs">{lead.phone || '—'}</td>
                                        <td className="px-4 py-2.5">
                                            {stageObj ? (
                                                <Badge className={`${stageObj.color} text-white text-[10px]`}>{stageObj.label}</Badge>
                                            ) : (
                                                <Badge variant="outline" className="text-[10px]">{lead.pipeline_stage}</Badge>
                                            )}
                                        </td>
                                        <td className="px-4 py-2.5 text-xs">{lead.interested_course_name || lead.course_interest || '—'}</td>
                                        <td className="px-4 py-2.5 text-right font-mono text-xs">AED {(lead.sale_amount || lead.enrollment_amount || 0).toLocaleString()}</td>
                                        <td className="px-4 py-2.5 text-xs" onClick={(e) => e.stopPropagation()}>
                                            <TableReassignCell
                                                lead={lead}
                                                availableAgents={availableAgents}
                                                onQuickReassign={handleQuickReassign}
                                                canReassign={isSuperAdmin || isTeamLeader}
                                            />
                                        </td>
                                        <td
                                            className="px-4 py-2.5 text-xs text-muted-foreground whitespace-nowrap"
                                            data-testid={`sales-table-assigned-${idx}`}
                                            title={assignedTs ? new Date(assignedTs).toLocaleString('en-GB') : ''}
                                        >
                                            {assignedTs
                                                ? new Date(assignedTs).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', hour12: false })
                                                : '—'}
                                        </td>
                                        <td className="px-4 py-2.5 text-xs text-muted-foreground">
                                            {lead.updated_at ? new Date(lead.updated_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }) : '—'}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                    {leads.length === 0 && (
                        <div className="text-center py-12 text-muted-foreground">No leads found</div>
                    )}
                </div>
            ) : (
                <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragStart={handleDragStart}
                    onDragEnd={handleDragEnd}
                >
                    <div className="kanban-board">
                        {visibleLeadStages.map((stage) => (
                            <KanbanColumn
                                key={stage.id}
                                stage={stage}
                                stageData={kanbanData[stage.id] || { items: [], total: 0, page: 1, total_pages: 1 }}
                                onUpdate={handleUpdateLead}
                                onView={handleViewLead}
                                onSetReminder={handleSetReminder}
                                isSuperAdmin={isSuperAdmin || isTeamLeader}
                                availableAgents={availableAgents}
                                onQuickReassign={handleQuickReassign}
                                onPageChange={handleKanbanPageChange}
                            />
                        ))}
                    </div>
                    <DragOverlay>
                        {activeId ? (
                            <div className="opacity-80">
                                <LeadCard
                                    lead={leads.find(l => l.id === activeId)}
                                    onUpdate={() => {}}
                                    onView={() => {}}
                                    onSetReminder={() => {}}
                                    isDragging={true}
                                />
                            </div>
                        ) : null}
                    </DragOverlay>
                </DndContext>
            )}
            
            {/* Pagination - Table mode only */}
            {!loading && displayMode === 'table' && leads.length > 0 && (
                <Pagination
                    page={currentPage}
                    totalPages={totalPages}
                    total={totalRecords}
                    pageSize={pageSize}
                    onPageChange={(p) => setCurrentPage(p)}
                    onPageSizeChange={(s) => { setPageSize(s); setCurrentPage(1); }}
                />
            )}

            {/* Advanced Search Fallback - When no results found */}
            {!loading && searchTerm.trim() && leads.length === 0 && (
                <Card className="border-dashed border-2 border-muted-foreground/30" data-testid="no-results-fallback">
                    <CardContent className="py-6">
                        <div className="text-center space-y-3">
                            <Search className="h-8 w-8 mx-auto text-muted-foreground" />
                            <p className="text-sm text-muted-foreground">No leads found for "{searchTerm}" in your view</p>
                            <p className="text-xs text-muted-foreground">Try a deep search across all leads by phone, email, or name</p>
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={handleDeepSearch}
                                disabled={deepSearching}
                                data-testid="deep-search-btn"
                            >
                                {deepSearching ? (
                                    <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-primary mr-2" />
                                ) : (
                                    <Search className="h-4 w-4 mr-2" />
                                )}
                                Search All Leads (Phone / Email / Name)
                            </Button>
                        </div>
                        {deepSearchResults !== null && (
                            <div className="mt-4">
                                {deepSearchResults.length > 0 ? (
                                    <div className="space-y-2 max-h-80 overflow-y-auto">
                                        <p className="text-xs font-medium text-muted-foreground mb-2">{deepSearchResults.length} result(s) found:</p>
                                        {deepSearchResults.map((lead) => (
                                            <div
                                                key={lead.id}
                                                className="flex items-center justify-between p-3 bg-muted/50 rounded-lg border cursor-pointer hover:bg-muted transition-colors"
                                                onClick={() => handleViewLead(lead)}
                                                data-testid={`deep-search-result-${lead.id}`}
                                            >
                                                <div className="flex items-center gap-3">
                                                    <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center text-white text-sm font-medium">
                                                        {lead.full_name?.charAt(0) || '?'}
                                                    </div>
                                                    <div>
                                                        <p className="text-sm font-medium">{lead.full_name}</p>
                                                        <div className="flex items-center gap-3 text-xs text-muted-foreground">
                                                            {lead.phone && <span className="flex items-center gap-1"><Phone className="h-3 w-3" />{lead.phone}</span>}
                                                            {lead.email && <span className="flex items-center gap-1"><Mail className="h-3 w-3" />{lead.email}</span>}
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <Badge variant="secondary" className="text-[10px]">{lead.stage?.replace(/_/g, ' ')}</Badge>
                                                    {lead.assigned_to_name && (
                                                        <span className="text-xs text-muted-foreground">{lead.assigned_to_name}</span>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <p className="text-center text-sm text-muted-foreground mt-2">No leads found across the entire database</p>
                                )}
                            </div>
                        )}
                    </CardContent>
                </Card>
            )}

            {/* Create Lead Modal */}
            <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
                <DialogContent className="max-w-lg">
                    <DialogHeader>
                        <DialogTitle>Create New Lead</DialogTitle>
                        <DialogDescription>
                            Add a new lead to the sales pipeline
                        </DialogDescription>
                    </DialogHeader>
                    
                    <form onSubmit={handleCreateLead} className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label htmlFor="full_name">Full Name *</Label>
                                <Input
                                    id="full_name"
                                    value={formData.full_name}
                                    onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                                    placeholder="John Doe"
                                    data-testid="lead-name-input"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="phone">Phone Number *</Label>
                                <Input
                                    id="phone"
                                    value={formData.phone}
                                    onChange={(e) => handlePhoneChange(e.target.value)}
                                    placeholder="+971 50 000 0000"
                                    data-testid="lead-phone-input"
                                />
                                {formData.country && formData.phone && (
                                    <p className="text-xs text-muted-foreground flex items-center gap-1">
                                        <MapPin className="h-3 w-3" />
                                        Auto-detected: {formData.country}
                                    </p>
                                )}
                            </div>
                        </div>
                        
                        <div className="space-y-2">
                            <Label htmlFor="email">Email</Label>
                            <Input
                                id="email"
                                type="email"
                                value={formData.email}
                                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                placeholder="john@example.com"
                                data-testid="lead-email-input"
                            />
                        </div>
                        
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label htmlFor="country">Country</Label>
                                <Select
                                    value={formData.country}
                                    onValueChange={(value) => setFormData({ ...formData, country: value })}
                                >
                                    <SelectTrigger data-testid="lead-country-select">
                                        <SelectValue placeholder="Select country" />
                                    </SelectTrigger>
                                    <SelectContent className="max-h-60">
                                        {COUNTRIES.map((country) => (
                                            <SelectItem key={country} value={country}>
                                                {country}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="lead_source">Lead Source</Label>
                                <Select
                                    value={formData.lead_source}
                                    onValueChange={(value) => setFormData({ ...formData, lead_source: value })}
                                >
                                    <SelectTrigger data-testid="lead-source-select">
                                        <SelectValue placeholder="Select source" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {LEAD_SOURCES.map((source) => (
                                            <SelectItem key={source.id} value={source.id}>
                                                {source.label}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                        
                        {(isSuperAdmin || isTeamLeader) && (
                            <div className="space-y-2">
                                <Label htmlFor="assigned_to">Assign to Agent (Optional)</Label>
                                <Select
                                    value={formData.assigned_to}
                                    onValueChange={(value) => setFormData({ ...formData, assigned_to: value })}
                                >
                                    <SelectTrigger data-testid="lead-assign-agent-select">
                                        <SelectValue placeholder="Auto (Round Robin)" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="round_robin">Auto (Round Robin)</SelectItem>
                                        {availableAgents.map((agent) => {
                                            const roleLabel = agent.role === 'team_leader' ? 'TL'
                                                : agent.role === 'sales_manager' ? 'SM'
                                                : agent.role === 'mentor' ? 'Mentor'
                                                : agent.role === 'master_of_academics' || agent.role === 'master_of_academics_' ? 'MoA'
                                                : '';
                                            return (
                                                <SelectItem key={agent.id} value={agent.id}>
                                                    {agent.full_name} {roleLabel ? `[${roleLabel}]` : ''} {agent.team_name ? `(${agent.team_name})` : ''}
                                                </SelectItem>
                                            );
                                        })}
                                    </SelectContent>
                                </Select>
                                <p className="text-xs text-muted-foreground">Leave as Auto to use round-robin assignment</p>
                            </div>
                        )}

                        <div className="space-y-2">
                            <Label htmlFor="notes">Notes</Label>
                            <Textarea
                                id="notes"
                                value={formData.notes}
                                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                                placeholder="Add any additional notes..."
                                rows={3}
                                data-testid="lead-notes-input"
                            />
                        </div>
                        
                        <DialogFooter>
                            <Button type="button" variant="outline" onClick={() => setShowCreateModal(false)}>
                                Cancel
                            </Button>
                            <Button type="submit" data-testid="submit-lead-btn">
                                Create Lead
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Lead Detail Modal */}
            <Dialog open={showDetailModal} onOpenChange={(open) => {
                setShowDetailModal(open);
                if (!open) {
                    // Wipe per-lead draft state on close so it can never bleed
                    // into the next lead the user opens.
                    setNewNote('');
                    setLeadNotes([]);
                    setSelectedLead(null);
                }
            }}>
                <DialogContent className="max-w-2xl">
                    <DialogHeader>
                        <DialogTitle>Lead Details</DialogTitle>
                        <DialogDescription>
                            View and update lead information
                        </DialogDescription>
                    </DialogHeader>
                    
                    {selectedLead && (
                        <ErrorBoundary label="Lead Details">
                        <div className="space-y-6">
                            {/* Lead Info */}
                            <Card>
                                <CardContent className="pt-6">
                                    <div className="flex items-start gap-4">
                                        <div className="w-16 h-16 rounded-full bg-blue-600 flex items-center justify-center text-white text-2xl font-bold">
                                            {selectedLead.full_name?.charAt(0)}
                                        </div>
                                        <div className="flex-1">
                                            {editingName ? (
                                                <div className="flex items-center gap-2">
                                                    <Input
                                                        value={editNameValue}
                                                        onChange={e => setEditNameValue(e.target.value)}
                                                        onKeyDown={e => { if (e.key === 'Enter') handleSaveName(); if (e.key === 'Escape') setEditingName(false); }}
                                                        autoFocus
                                                        className="text-lg font-semibold h-9"
                                                        data-testid="edit-lead-name-input"
                                                    />
                                                    <Button size="sm" onClick={handleSaveName} data-testid="save-lead-name-btn">Save</Button>
                                                    <Button size="sm" variant="ghost" onClick={() => setEditingName(false)}>Cancel</Button>
                                                </div>
                                            ) : (
                                                <h3 className="text-xl font-semibold group flex items-center gap-2">
                                                    {selectedLead.full_name}
                                                    {canEditName && (
                                                        <button
                                                            onClick={() => { setEditNameValue(selectedLead.full_name); setEditingName(true); }}
                                                            className="opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-foreground"
                                                            title="Edit name"
                                                            data-testid="edit-lead-name-btn"
                                                        >
                                                            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z"/></svg>
                                                        </button>
                                                    )}
                                                </h3>
                                            )}
                                            <div className="flex items-center gap-4 mt-2 text-muted-foreground">
                                                <span className="flex items-center gap-2">
                                                    <Phone className="h-4 w-4" />
                                                    {selectedLead.phone}
                                                    <ClickToCall 
                                                        phoneNumber={selectedLead.phone} 
                                                        contactId={selectedLead.id} 
                                                        contactName={selectedLead.full_name}
                                                        variant="outline"
                                                        size="sm"
                                                        showLabel={true}
                                                        className="ml-2"
                                                    />
                                                    {(() => {
                                                        const p = pickupForPhone(selectedPickup.byPhone, selectedLead.phone);
                                                        if (!p) return null;
                                                        const isWin = pickupForPhone(
                                                            { [selectedPickup.winnerPhone]: true },
                                                            selectedLead.phone
                                                        ) === true;
                                                        return <LastPickupBadge pickup={p} isWinner={isWin} compact />;
                                                    })()}
                                                </span>
                                                {selectedLead.email && (
                                                    <span className="flex items-center gap-1">
                                                        <Mail className="h-4 w-4" />
                                                        {selectedLead.email}
                                                    </span>
                                                )}
                                            </div>
                                            <div className="flex items-center gap-2 mt-3">
                                                <Badge className={ALL_LEAD_STAGES.find(s => s.id === selectedLead.stage)?.color}>
                                                    {ALL_LEAD_STAGES.find(s => s.id === selectedLead.stage)?.label || selectedLead.stage}
                                                </Badge>
                                                {selectedLead.sla_breach && (
                                                    <Badge className="bg-red-500">SLA Breach</Badge>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Always-visible Secondary Phone editor.
                                        Lives outside notes so notes can NEVER
                                        overwrite it. Saved to the dedicated
                                        `secondary_phone` field. */}
                                    <SecondaryPhoneField
                                        leadId={selectedLead.id}
                                        value={selectedLead.secondary_phone || ''}
                                        primaryPhone={selectedLead.phone}
                                        pickup={pickupForPhone(selectedPickup.byPhone, selectedLead.secondary_phone)}
                                        isWinner={
                                            !!selectedLead.secondary_phone &&
                                            pickupForPhone(
                                                { [selectedPickup.winnerPhone]: true },
                                                selectedLead.secondary_phone
                                            ) === true
                                        }
                                        onRefresh={(newValue) => {
                                            // Optimistically reflect the new value in
                                            // the open modal so the user sees it
                                            // immediately, then trigger a kanban
                                            // refetch so the closed card pill updates
                                            // too.
                                            setSelectedLead(prev => prev
                                                ? { ...prev, secondary_phone: newValue }
                                                : prev);
                                            fetchLeads();
                                        }}
                                    />
                                    <AdditionalNumbersField
                                        leadId={selectedLead.id}
                                        value={selectedLead.additional_numbers || []}
                                        leadName={selectedLead.full_name}
                                        byPickup={selectedPickup.byPhone}
                                        winnerPhone={selectedPickup.winnerPhone}
                                        onRefresh={(newList) => {
                                            setSelectedLead(prev => prev
                                                ? { ...prev, additional_numbers: newList }
                                                : prev);
                                            fetchLeads();
                                        }}
                                    />
                                    
                                    <div className="grid grid-cols-2 gap-4 mt-6 text-sm">
                                        <div>
                                            <span className="text-muted-foreground">Country:</span>
                                            <span className="ml-2">{selectedLead.country || 'N/A'}</span>
                                        </div>
                                        <div>
                                            <span className="text-muted-foreground">Lead Source:</span>
                                            <span className="ml-2">{selectedLead.lead_source || 'N/A'}</span>
                                        </div>
                                        <div>
                                            <span className="text-muted-foreground">Course Interest:</span>
                                            <span className="ml-2">{selectedLead.course_of_interest || 'N/A'}</span>
                                        </div>
                                        <div>
                                            <span className="text-muted-foreground">Assigned To:</span>
                                            <span className="ml-2">{selectedLead.assigned_to_name || 'Unassigned'}</span>
                                        </div>
                                        <div>
                                            <span className="text-muted-foreground">Created:</span>
                                            <span className="ml-2">{formatDate(selectedLead.created_at)}</span>
                                        </div>
                                        <div>
                                            <span className="text-muted-foreground">Last Activity:</span>
                                            <span className="ml-2">{formatDate(selectedLead.last_activity)}</span>
                                        </div>
                                        <NumberConflictBadge lead={selectedLead} />
                                        <LeadOriginStamp lead={selectedLead} />
                                    </div>
                                    
                                    {/* Call Recordings */}
                                    <LeadCallRecordings leadId={selectedLead.id} />
                                    
                                    {selectedLead.notes && (
                                        <div className="mt-4 p-3 bg-muted rounded-lg">
                                            <span className="text-muted-foreground text-sm">Notes:</span>
                                            {/* Legacy leads stored `notes` as an array of note objects; modern leads store a string. Handle both safely. */}
                                            {Array.isArray(selectedLead.notes) ? (
                                                <div className="mt-1 space-y-1">
                                                    {selectedLead.notes.map((n, i) => (
                                                        <p key={n?.id || i} className="text-sm whitespace-pre-wrap">
                                                            {typeof n === 'string' ? n : (n?.text || '')}
                                                        </p>
                                                    ))}
                                                </div>
                                            ) : typeof selectedLead.notes === 'object' ? (
                                                <p className="mt-1">{selectedLead.notes?.text || ''}</p>
                                            ) : (
                                                <p className="mt-1">{selectedLead.notes}</p>
                                            )}
                                        </div>
                                    )}
                                </CardContent>
                            </Card>
                            
                            {/* Update Section */}
                            <Card>
                                <CardHeader>
                                    <CardTitle className="text-lg">Update Lead</CardTitle>
                                </CardHeader>
                                <CardContent className="space-y-4">
                                    <div className="grid grid-cols-2 gap-4">
                                        <div className="space-y-2">
                                            <Label>Move to Stage</Label>
                                            <Select
                                                value={updateData.stage}
                                                onValueChange={(value) => setUpdateData({ ...updateData, stage: value })}
                                            >
                                                <SelectTrigger data-testid="update-stage-select">
                                                    <SelectValue placeholder="Select stage" />
                                                </SelectTrigger>
                                                <SelectContent position="popper" className="z-[9999]">
                                                    {ALL_LEAD_STAGES.map((stage) => (
                                                        <SelectItem key={stage.id} value={stage.id}>
                                                            {stage.label}
                                                        </SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        </div>
                                        
                                        {(updateData.stage === 'rejected' || updateData.stage === 'not_interested') && (
                                            <div className="space-y-2">
                                                <Label>Rejection Reason *</Label>
                                                <Select
                                                    value={updateData.rejection_reason}
                                                    onValueChange={(value) => setUpdateData({ ...updateData, rejection_reason: value })}
                                                >
                                                    <SelectTrigger data-testid="rejection-reason-select">
                                                        <SelectValue placeholder="Select reason" />
                                                    </SelectTrigger>
                                                    <SelectContent position="popper" className="z-[9999]">
                                                        {REJECTION_REASONS.map((reason) => (
                                                            <SelectItem key={reason.id} value={reason.id}>
                                                                {reason.label}
                                                            </SelectItem>
                                                        ))}
                                                    </SelectContent>
                                                </Select>
                                            </div>
                                        )}
                                        
                                        {/* Follow-up Date - always available */}
                                        <div className="space-y-2">
                                            <Label>Follow-up Date & Time</Label>
                                            <Input
                                                type="datetime-local"
                                                value={updateData.follow_up_date}
                                                onChange={(e) => setUpdateData({ ...updateData, follow_up_date: e.target.value })}
                                                data-testid="follow-up-date-input"
                                            />
                                            {selectedLead?.follow_up_date && (
                                                <p className="text-xs text-muted-foreground">
                                                    Current follow-up: {new Date(selectedLead.follow_up_date).toLocaleString('en-AE', { dateStyle: 'medium', timeStyle: 'short' })}
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                    
                                    {/* Course & Add-ons Selection - Required for pipeline stages */}
                                    {PIPELINE_STAGES.includes(updateData.stage) && (
                                        <div className="p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg space-y-4">
                                            <div className="flex items-center gap-2 text-amber-600">
                                                <Target className="h-4 w-4" />
                                                <span className="text-sm font-medium">Course & Add-ons Selection</span>
                                            </div>
                                            
                                            {/* Course Selection */}
                                            <div className="space-y-2">
                                                <Label className="flex items-center gap-1.5">
                                                    <Package className="h-3.5 w-3.5" />
                                                    Select Course *
                                                </Label>
                                                <Select
                                                    value={updateData.interested_course_id}
                                                    onValueChange={(value) => {
                                                        const course = catalogCourses.find(c => c.id === value);
                                                        const coursePrice = course?.price || 0;
                                                        const addonsPrice = (updateData.selectedAddons || []).reduce((sum, a) => sum + (a.price || 0), 0);
                                                        setUpdateData({ 
                                                            ...updateData, 
                                                            interested_course_id: value,
                                                            estimated_value: coursePrice + addonsPrice
                                                        });
                                                    }}
                                                >
                                                    <SelectTrigger data-testid="course-interest-select">
                                                        <SelectValue placeholder="Select course" />
                                                    </SelectTrigger>
                                                    <SelectContent position="popper" className="z-[9999]">
                                                        {catalogCourses.map((course) => (
                                                            <SelectItem key={course.id} value={course.id}>
                                                                {course.name} - AED {course.price?.toLocaleString()}
                                                            </SelectItem>
                                                        ))}
                                                    </SelectContent>
                                                </Select>
                                            </div>
                                            
                                            {/* Add-ons Selection (Multi-select checkboxes) */}
                                            {catalogAddons.length > 0 && (
                                                <div className="space-y-2">
                                                    <Label className="flex items-center gap-1.5">
                                                        <ShoppingCart className="h-3.5 w-3.5" />
                                                        Select Add-ons (Optional)
                                                    </Label>
                                                    <div className="grid grid-cols-2 gap-2 p-3 bg-background rounded-md border border-border">
                                                        {catalogAddons.map((addon) => {
                                                            const isSelected = (updateData.selectedAddons || []).some(a => a.id === addon.id);
                                                            return (
                                                                <label
                                                                    key={addon.id}
                                                                    className={`flex items-center gap-2 p-2 rounded-md cursor-pointer transition-colors text-sm ${
                                                                        isSelected 
                                                                            ? 'bg-primary/10 border border-primary/30' 
                                                                            : 'hover:bg-muted/50 border border-transparent'
                                                                    }`}
                                                                    data-testid={`addon-checkbox-${addon.id}`}
                                                                >
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={isSelected}
                                                                        onChange={(e) => {
                                                                            let newAddons;
                                                                            if (e.target.checked) {
                                                                                newAddons = [...(updateData.selectedAddons || []), { id: addon.id, name: addon.name, price: addon.price }];
                                                                            } else {
                                                                                newAddons = (updateData.selectedAddons || []).filter(a => a.id !== addon.id);
                                                                            }
                                                                            const coursePrice = catalogCourses.find(c => c.id === updateData.interested_course_id)?.price || 0;
                                                                            const addonsPrice = newAddons.reduce((sum, a) => sum + (a.price || 0), 0);
                                                                            setUpdateData({
                                                                                ...updateData,
                                                                                selectedAddons: newAddons,
                                                                                estimated_value: coursePrice + addonsPrice
                                                                            });
                                                                        }}
                                                                        className="rounded border-gray-400 text-primary focus:ring-primary h-4 w-4"
                                                                    />
                                                                    <span className="flex-1 truncate">{(addon.name || 'Add-on').replace('Addons - ', '')}</span>
                                                                    <span className="text-xs font-medium text-muted-foreground whitespace-nowrap">AED {addon.price || 0}</span>
                                                                </label>
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                            )}
                                            
                                            {/* Price Breakdown */}
                                            {updateData.interested_course_id && (
                                                <div className="p-3 bg-background rounded-md border border-border space-y-1.5" data-testid="price-breakdown">
                                                    <div className="flex justify-between text-sm">
                                                        <span className="text-muted-foreground">Course</span>
                                                        <span>AED {(catalogCourses.find(c => c.id === updateData.interested_course_id)?.price || 0).toLocaleString()}</span>
                                                    </div>
                                                    {(updateData.selectedAddons || []).length > 0 && (
                                                        <>
                                                            {(updateData.selectedAddons || []).map((a) => (
                                                                <div key={a.id} className="flex justify-between text-sm">
                                                                    <span className="text-muted-foreground">{(a.name || 'Add-on').replace('Addons - ', '')}</span>
                                                                    <span>AED {a.price?.toLocaleString() || 0}</span>
                                                                </div>
                                                            ))}
                                                        </>
                                                    )}
                                                    <div className="flex justify-between text-sm font-semibold pt-1.5 border-t border-border">
                                                        <span>Estimated Total</span>
                                                        <span className="text-primary">AED {(updateData.estimated_value || 0).toLocaleString()}</span>
                                                    </div>
                                                </div>
                                            )}
                                            
                                            {selectedLead?.interested_course_name && (
                                                <p className="text-xs text-muted-foreground">
                                                    Currently interested in: <span className="font-medium">{selectedLead.interested_course_name}</span>
                                                    {selectedLead?.selected_addons?.length > 0 && (
                                                        <span> + {selectedLead.selected_addons.map(a => a.name?.replace('Addons - ', '')).join(', ')}</span>
                                                    )}
                                                </p>
                                            )}
                                        </div>
                                    )}
                                    
                                    <div className="space-y-3" data-testid="lead-notes-section">
                                        <Label className="flex items-center gap-1.5"><MessageSquare className="h-3.5 w-3.5" /> Notes History</Label>
                                        {/* New note input */}
                                        <div className="flex gap-2">
                                            <Textarea
                                                value={newNote}
                                                onChange={(e) => setNewNote(e.target.value)}
                                                placeholder="Write a note..."
                                                rows={2}
                                                className="flex-1 text-sm"
                                                data-testid="new-note-input"
                                                onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleAddNote(); }}}
                                            />
                                            <Button size="sm" onClick={handleAddNote} disabled={!newNote.trim() || noteSaving} className="self-end" data-testid="save-note-btn">
                                                <Send className="h-3.5 w-3.5" />
                                            </Button>
                                        </div>
                                        {/* Notes history thread */}
                                        <ScrollArea className="max-h-[220px]">
                                            {notesLoading ? (
                                                <p className="text-xs text-muted-foreground text-center py-3">Loading notes...</p>
                                            ) : leadNotes.length === 0 ? (
                                                <p className="text-xs text-muted-foreground text-center py-3">No notes yet. Add the first note above.</p>
                                            ) : (
                                                <div className="space-y-2 pr-2">
                                                    {leadNotes.map((note) => (
                                                        <div key={note.id} className="p-2.5 rounded-md bg-muted/50 border border-border/50" data-testid={`note-${note.id}`}>
                                                            <p className="text-sm whitespace-pre-wrap">{note.text}</p>
                                                            <div className="flex items-center gap-2 mt-1.5 text-[10px] text-muted-foreground">
                                                                <span className="font-medium">{note.created_by_name}</span>
                                                                <span>&middot;</span>
                                                                <span>{new Date(note.created_at).toLocaleString('en-AE', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </ScrollArea>
                                    </div>
                                </CardContent>
                            </Card>
                            
                            <DialogFooter>
                                <Button variant="outline" onClick={() => setShowDetailModal(false)}>
                                    Cancel
                                </Button>
                                <Button onClick={handleUpdateLead} data-testid="update-lead-btn">
                                    Update Lead
                                </Button>
                            </DialogFooter>
                        </div>
                        </ErrorBoundary>
                    )}
                </DialogContent>
            </Dialog>

            {/* Reminder Modal */}
            <ReminderModal
                open={showReminderModal}
                onClose={() => { setShowReminderModal(false); setReminderLead(null); }}
                entityType="lead"
                entityId={reminderLead?.id}
                entityName={reminderLead?.full_name}
                onSuccess={handleReminderSuccess}
            />

            {/* Enrollment Payment Modal */}
            <EnrollmentPaymentModal
                open={showEnrollmentModal}
                onClose={() => { setShowEnrollmentModal(false); setPendingEnrollmentLead(null); }}
                lead={pendingEnrollmentLead}
                course={catalogCourses.find(c => c.id === pendingEnrollmentLead?.interested_course_id) || courses.find(c => c.id === pendingEnrollmentLead?.interested_course_id)}
                onComplete={handleEnrollmentComplete}
            />

            {/* Rejection Reason Modal */}
            <RejectionReasonModal
                open={showRejectionModal}
                onClose={() => { setShowRejectionModal(false); setPendingRejectionLead(null); }}
                lead={pendingRejectionLead}
                onComplete={handleRejectionComplete}
            />

            {/* Reactivate Rejected Lead Modal (super_admin / sales_manager only) */}
            <ReactivateLeadModal
                open={showReactivateModal}
                lead={pendingReactivateLead}
                defaultStage={pendingReactivateStage}
                onClose={() => { setShowReactivateModal(false); setPendingReactivateLead(null); setPendingReactivateStage(null); }}
                onSuccess={() => { fetchLeads(); }}
            />

            {/* Duplicate Lead Dialog */}
            <Dialog open={showDuplicateDialog} onOpenChange={setShowDuplicateDialog}>
                <DialogContent className="max-w-lg" data-testid="duplicate-dialog">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-amber-500">
                            <AlertTriangle className="h-5 w-5" />
                            Duplicate Lead Found
                        </DialogTitle>
                    </DialogHeader>
                    {duplicateInfo?.existing_lead && (() => {
                        const el = duplicateInfo.existing_lead;
                        return (
                            <div className="space-y-4">
                                <p className="text-sm text-muted-foreground">
                                    {duplicateInfo.matched_on === 'secondary_phone'
                                        ? <span className="text-rose-700 font-medium" data-testid="dup-secondary-warning">The <strong>secondary number</strong> you entered already belongs to the lead below. Do not add it — ask your team leader to resolve which lead keeps the number.</span>
                                        : <>A lead with the same <strong>{duplicateInfo.matched_on}</strong> already exists in the system.</>}
                                </p>
                                <div className="bg-muted/30 rounded-lg p-4 space-y-2.5">
                                    <div className="flex items-center gap-2">
                                        <User className="h-4 w-4 text-muted-foreground" />
                                        <span className="font-semibold">{el.full_name}</span>
                                    </div>
                                    <div className="grid grid-cols-2 gap-2 text-sm">
                                        <div className="flex items-center gap-1.5"><Phone className="h-3.5 w-3.5 text-muted-foreground" />{el.phone || 'N/A'}</div>
                                        <div className="flex items-center gap-1.5"><Mail className="h-3.5 w-3.5 text-muted-foreground" />{el.email || 'N/A'}</div>
                                    </div>
                                    <div className="flex items-center gap-4 text-sm text-muted-foreground">
                                        <Badge variant="outline" className="capitalize">{el.stage?.replace(/_/g, ' ')}</Badge>
                                        <span>Assigned to: {el.assigned_to_name || 'Unassigned'}</span>
                                    </div>
                                    {el.source && <p className="text-xs text-muted-foreground">Source: {el.source}</p>}
                                </div>
                                <p className="text-sm">Would you like to <strong>merge</strong> the new data into the existing lead? Only missing fields will be updated.</p>
                                <div className="flex gap-2 justify-end">
                                    <Button variant="outline" onClick={() => { setShowDuplicateDialog(false); setShowCreateModal(true); }} data-testid="duplicate-cancel-btn">
                                        Go Back
                                    </Button>
                                    <Button onClick={handleMerge} disabled={merging} className="bg-amber-600 hover:bg-amber-700" data-testid="duplicate-merge-btn">
                                        <GitMerge className="h-4 w-4 mr-2" />{merging ? 'Merging...' : 'Merge Lead'}
                                    </Button>
                                </div>
                            </div>
                        );
                    })()}
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default SalesCRMPage;
